/**
 * ResizeHandle Component
 *
 * A draggable handle for resizing panels.
 * Follows the same drag interaction pattern as Timeline.tsx for consistency.
 *
 * USAGE:
 * - Place at panel boundaries
 * - Vertical handles resize left/right
 * - Horizontal handles resize up/down
 */

import { useState, useCallback, useEffect, CSSProperties } from 'react';
import { ResizeHandleProps } from '@/types';

function ResizeHandle({
  direction,
  onResize,
  onResizeStart,
  onResizeEnd,
  style,
}: ResizeHandleProps) {
  // Track if we're currently dragging
  const [isDragging, setIsDragging] = useState(false);

  // Track the initial mouse position when dragging starts
  const [startPos, setStartPos] = useState(0);

  /**
   * Handle mouse down - start dragging
   */
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      // Only left click
      if (event.button !== 0) return;

      event.preventDefault();
      setIsDragging(true);

      // Store initial position (x for vertical, y for horizontal)
      setStartPos(direction === 'vertical' ? event.clientX : event.clientY);

      // Call start callback if provided
      onResizeStart?.();
    },
    [direction, onResizeStart]
  );

  /**
   * Handle mouse move - update resize delta
   */
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;

      const currentPos = direction === 'vertical' ? event.clientX : event.clientY;
      const delta = currentPos - startPos;

      // Update start position for next frame
      setStartPos(currentPos);

      // Call resize callback with delta
      onResize(delta);
    },
    [isDragging, startPos, direction, onResize]
  );

  /**
   * Handle mouse up - stop dragging
   */
  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    // Call end callback if provided
    onResizeEnd?.();
  },  [isDragging, onResizeEnd]);

  // Set up global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Base styles for the handle
  const baseStyles: CSSProperties = {
    position: 'absolute',
    zIndex: 100,
    transition: isDragging ? 'none' : 'background-color var(--nb-transition-fast)',
    backgroundColor: isDragging
      ? 'var(--nb-accent-primary)'
      : 'transparent',
    cursor: direction === 'vertical' ? 'col-resize' : 'row-resize',
  };

  // Direction-specific styles
  const directionStyles: CSSProperties =
    direction === 'vertical'
      ? {
          width: '5px',
          height: '100%',
          top: 0,
        }
      : {
          width: '100%',
          height: '5px',
          left: 0,
        };

  // Hover styles
  const hoverStyles = `
    .resize-handle:hover {
      background-color: var(--nb-border-secondary);
    }
  `;

  return (
    <>
      <style>{hoverStyles}</style>
      <div
        className="resize-handle"
        onMouseDown={handleMouseDown}
        style={{
          ...baseStyles,
          ...directionStyles,
          ...style,
        }}
        title={
          direction === 'vertical'
            ? 'Drag to resize panel width'
            : 'Drag to resize panel height'
        }
      />
    </>
  );
}

export default ResizeHandle;
